import { useMemo, useState, type ReactNode } from 'react';
import {
  BarChart3,
  PieChart,
  Stethoscope,
  CheckCircle2,
  Ban,
  Users,
  Printer,
  FileSpreadsheet,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Building2,
  UserCog,
  Syringe,
  Package,
  Pill,
  CakeSlice,
  CalendarRange,
  X,
} from 'lucide-react';
import {
  ENTIDADES_CATALOG,
  EPISODE_STATUS_LABELS,
  EpisodeStatusSchema,
  listProcedureNames,
  type DashboardCountItem,
  type DashboardItemUsage,
  type EpisodeStatus,
  type Report,
  type ReportsQuery,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePageTitle } from '@/shared/store/page';
import { cn } from '@/shared/lib/cn';
import { downloadCsv } from '@/shared/lib/csv';
import { useUsersList } from '@/features/users/api';
import { useReport } from './api';

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const TODAY_ISO = now.toISOString().slice(0, 10);

interface Filters {
  desde: string;
  hasta: string;
  medicoId: string;
  entidad: string;
  tipo: string;
  estado: EpisodeStatus | '';
}

const DEFAULT_FILTERS: Filters = {
  desde: `${CURRENT_YEAR}-01-01`,
  hasta: TODAY_ISO,
  medicoId: '',
  entidad: '',
  tipo: '',
  estado: '',
};

export function ReportsPage(): JSX.Element {
  usePageTitle('Reportes', 'Gestión · Análisis operativo');

  const [draft, setDraft] = useState<Filters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS);

  const query: ReportsQuery = useMemo(
    () => ({
      ...(applied.desde !== '' ? { desde: applied.desde } : {}),
      ...(applied.hasta !== '' ? { hasta: applied.hasta } : {}),
      ...(applied.medicoId !== '' ? { medicoId: applied.medicoId } : {}),
      ...(applied.entidad !== '' ? { entidad: applied.entidad } : {}),
      ...(applied.tipo !== '' ? { tipo: applied.tipo } : {}),
      ...(applied.estado !== '' ? { estado: applied.estado } : {}),
    }),
    [applied],
  );

  const reportQuery = useReport(query);
  const data = reportQuery.data;

  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const medicos = (usersQuery.data?.items ?? []).filter((u) => u.rol === 'medico');
  const tipos = useMemo(() => listProcedureNames(), []);
  const selectCls =
    'h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

  const rangeLabel = `${applied.desde || '—'} a ${applied.hasta || '—'}`;

  return (
    <div className="flex flex-col gap-5">
      {/* Encabezado solo para impresión */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-extrabold">Reporte operativo — Endomedical IPS</h1>
        <p className="text-sm text-text-muted">Periodo: {rangeLabel}</p>
      </div>

      {/* Toolbar de filtros + export */}
      <Card className="print:hidden">
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarRange className="w-4 h-4 text-primary" /> Filtros del reporte
            </CardTitle>
            <CardDescription>
              Acota por rango de fechas y otros criterios; luego exporta a Excel o PDF.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="secondary" size="sm" onClick={() => window.print()}>
              <Printer className="w-4 h-4" /> Descargar PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void reportQuery.refetch()}
              disabled={reportQuery.isFetching}
            >
              <RefreshCw className={cn('w-4 h-4', reportQuery.isFetching && 'animate-spin')} />
            </Button>
          </div>
        </CardHeader>
        <div className="px-6 pb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="desde">Desde</Label>
            <Input
              id="desde"
              type="date"
              value={draft.desde}
              onChange={(e) => setDraft({ ...draft, desde: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hasta">Hasta</Label>
            <Input
              id="hasta"
              type="date"
              value={draft.hasta}
              onChange={(e) => setDraft({ ...draft, hasta: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Estado</Label>
            <select
              value={draft.estado}
              onChange={(e) => setDraft({ ...draft, estado: e.target.value as EpisodeStatus | '' })}
              className={selectCls}
            >
              <option value="">Todos</option>
              {EpisodeStatusSchema.options.map((s) => (
                <option key={s} value={s}>
                  {EPISODE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Médico</Label>
            <SearchableSelect
              value={draft.medicoId}
              onChange={(v) => setDraft({ ...draft, medicoId: v })}
              placeholder="Todos"
              clearable
              options={medicos.map((m) => ({ value: m.id, label: m.nombre }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Entidad</Label>
            <SearchableSelect
              value={draft.entidad}
              onChange={(v) => setDraft({ ...draft, entidad: v })}
              placeholder="Todas"
              clearable
              options={ENTIDADES_CATALOG.map((ent) => ({ value: ent, label: ent }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Tipo</Label>
            <SearchableSelect
              value={draft.tipo}
              onChange={(v) => setDraft({ ...draft, tipo: v })}
              placeholder="Todos"
              clearable
              options={tipos.map((t) => ({ value: t, label: t }))}
            />
          </div>
          <div className="xl:col-span-6 flex items-center gap-2">
            <Button size="sm" onClick={() => setApplied(draft)}>
              Aplicar filtros
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(DEFAULT_FILTERS);
                setApplied(DEFAULT_FILTERS);
              }}
            >
              <X className="w-4 h-4" /> Restablecer
            </Button>
            <span className="text-xs text-text-muted ml-auto">Periodo aplicado: {rangeLabel}</span>
          </div>
        </div>
      </Card>

      {reportQuery.isError ? (
        <Card>
          <div className="p-8 text-center text-sm text-text-muted">
            No se pudo cargar el reporte.{' '}
            <button
              onClick={() => void reportQuery.refetch()}
              className="text-primary font-semibold underline"
            >
              Reintentar
            </button>
          </div>
        </Card>
      ) : data === undefined ? (
        <Card>
          <div className="p-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        </Card>
      ) : (
        <ReportBody data={data} rangeLabel={rangeLabel} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Body                                                                       */
/* -------------------------------------------------------------------------- */

function ReportBody({ data, rangeLabel }: { data: Report; rangeLabel: string }): JSX.Element {
  const delta = data.comparativo?.deltaPct ?? null;
  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
        <KpiCard
          label="Episodios"
          value={data.totales.episodios}
          icon={<Stethoscope className="w-[18px] h-[18px]" />}
          accent="indigo"
          delta={delta}
          deltaHint={
            data.comparativo !== null
              ? `vs ${data.comparativo.episodiosPrevios} periodo anterior`
              : undefined
          }
        />
        <KpiCard
          label="Cerrados"
          value={data.totales.cerrados}
          icon={<CheckCircle2 className="w-[18px] h-[18px]" />}
          accent="green"
        />
        <KpiCard
          label="Anulados"
          value={data.totales.anulados}
          icon={<Ban className="w-[18px] h-[18px]" />}
          accent="red"
        />
        <KpiCard
          label="Pacientes únicos"
          value={data.totales.pacientesUnicos}
          icon={<Users className="w-[18px] h-[18px]" />}
          accent="blue"
        />
      </div>

      {/* Mensual + estado */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5">
        <SectionCard
          title="Procedimientos por mes"
          icon={<BarChart3 className="w-4 h-4 text-primary" />}
          onExport={() =>
            downloadCsv(
              `reporte-por-mes-${rangeLabel}`,
              ['Mes', 'Episodios'],
              data.porMes.map((m) => [m.mes, m.count]),
            )
          }
        >
          <MonthlyBars data={data.porMes} />
        </SectionCard>
        <SectionCard
          title="Distribución por estado"
          icon={<PieChart className="w-4 h-4 text-primary" />}
          onExport={() =>
            downloadCsv(
              `reporte-por-estado-${rangeLabel}`,
              ['Estado', 'Episodios'],
              data.porEstado.map((s) => [estadoLabel(s.label), s.count]),
            )
          }
        >
          <Donut data={data.porEstado.map((s) => ({ label: estadoLabel(s.label), count: s.count }))} />
        </SectionCard>
      </div>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RankSection
          title="Por tipo de procedimiento"
          icon={<BarChart3 className="w-4 h-4 text-primary" />}
          data={data.porTipo}
          rangeLabel={rangeLabel}
          file="por-tipo"
          col="Tipo"
        />
        <RankSection
          title="Por médico"
          icon={<UserCog className="w-4 h-4 text-primary" />}
          data={data.porMedico}
          rangeLabel={rangeLabel}
          file="por-medico"
          col="Médico"
        />
        <RankSection
          title="Por anestesiólogo"
          icon={<Syringe className="w-4 h-4 text-primary" />}
          data={data.porAnestesiologo}
          rangeLabel={rangeLabel}
          file="por-anestesiologo"
          col="Anestesiólogo"
        />
        <RankSection
          title="Por entidad"
          icon={<Building2 className="w-4 h-4 text-primary" />}
          data={data.porEntidad}
          rangeLabel={rangeLabel}
          file="por-entidad"
          col="Entidad"
        />
        <RankSection
          title="Rango de edad"
          icon={<CakeSlice className="w-4 h-4 text-primary" />}
          data={data.rangosEdad}
          rangeLabel={rangeLabel}
          file="por-edad"
          col="Rango"
        />
        <UsageSection
          title="Insumos más utilizados"
          icon={<Package className="w-4 h-4 text-primary" />}
          data={data.topInsumos}
          rangeLabel={rangeLabel}
          file="insumos"
        />
        <UsageSection
          title="Medicamentos más utilizados"
          icon={<Pill className="w-4 h-4 text-primary" />}
          data={data.topMedicamentos}
          rangeLabel={rangeLabel}
          file="medicamentos"
        />
      </div>
    </>
  );
}

function estadoLabel(s: string): string {
  return (EPISODE_STATUS_LABELS as Record<string, string>)[s] ?? s;
}

/* -------------------------------------------------------------------------- */
/* Section wrappers                                                           */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  icon,
  onExport,
  children,
}: {
  title: string;
  icon: JSX.Element;
  onExport: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon} {title}
        </CardTitle>
        <Button variant="ghost" size="sm" className="print:hidden" onClick={onExport}>
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </Button>
      </CardHeader>
      <div className="p-6">{children}</div>
    </Card>
  );
}

function RankSection({
  title,
  icon,
  data,
  rangeLabel,
  file,
  col,
}: {
  title: string;
  icon: JSX.Element;
  data: DashboardCountItem[];
  rangeLabel: string;
  file: string;
  col: string;
}): JSX.Element {
  return (
    <SectionCard
      title={title}
      icon={icon}
      onExport={() =>
        downloadCsv(`reporte-${file}-${rangeLabel}`, [col, 'Episodios'], data.map((d) => [d.label, d.count]))
      }
    >
      <RankBars data={data.map((d) => ({ label: d.label, value: d.count }))} />
    </SectionCard>
  );
}

function UsageSection({
  title,
  icon,
  data,
  rangeLabel,
  file,
}: {
  title: string;
  icon: JSX.Element;
  data: DashboardItemUsage[];
  rangeLabel: string;
  file: string;
}): JSX.Element {
  return (
    <SectionCard
      title={title}
      icon={icon}
      onExport={() =>
        downloadCsv(
          `reporte-${file}-${rangeLabel}`,
          ['Código', 'Nombre', 'Cantidad'],
          data.map((d) => [d.codigo, d.nombre, d.cantidad]),
        )
      }
    >
      <RankBars
        data={data.map((d) => ({ label: `${d.codigo} · ${d.nombre}`, value: d.cantidad }))}
      />
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Charts                                                                     */
/* -------------------------------------------------------------------------- */

function RankBars({ data }: { data: { label: string; value: number }[] }): JSX.Element {
  const max = data.reduce((acc, d) => Math.max(acc, d.value), 0);
  if (data.length === 0) {
    return <div className="text-center text-sm text-text-muted py-6">Sin datos.</div>;
  }
  return (
    <div className="flex flex-col gap-2.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3 text-sm">
          <div className="w-[170px] text-text-soft truncate" title={d.label}>
            {d.label}
          </div>
          <div className="flex-1 h-7 bg-surface-alt rounded overflow-hidden relative">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent rounded"
              style={{ width: `${max === 0 ? 0 : (d.value / max) * 100}%` }}
            />
            <span className="absolute inset-0 flex items-center px-2 text-xs font-bold text-white mix-blend-luminosity tabular-nums">
              {Number.isInteger(d.value) ? d.value : d.value.toFixed(1)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MonthlyBars({ data }: { data: { mes: string; count: number }[] }): JSX.Element {
  const max = data.reduce((acc, d) => Math.max(acc, d.count), 0);
  if (data.length === 0) {
    return <div className="text-center text-sm text-text-muted py-6">Sin datos en el periodo.</div>;
  }
  return (
    <div className="flex items-end gap-2 h-48 overflow-x-auto">
      {data.map((d) => (
        <div key={d.mes} className="flex flex-col items-center gap-1 flex-1 min-w-[36px] h-full justify-end">
          <span className="text-[11px] font-bold tabular-nums text-text">{d.count}</span>
          <div
            className="w-full max-w-[40px] bg-gradient-to-t from-primary to-accent rounded-t"
            style={{ height: `${max === 0 ? 0 : Math.max(4, (d.count / max) * 100)}%` }}
          />
          <span className="text-[10px] text-text-muted">{d.mes.slice(2)}</span>
        </div>
      ))}
    </div>
  );
}

const DONUT_COLORS = ['#3aa393', '#3b82f6', '#a855f7', '#f59e0b', '#16a34a', '#dc2626', '#0ea5e9'];

function Donut({ data }: { data: { label: string; count: number }[] }): JSX.Element {
  const total = data.reduce((acc, d) => acc + d.count, 0);
  const radius = 56;
  const stroke = 20;
  const c = 2 * Math.PI * radius;
  let offset = 0;
  if (total === 0) {
    return <div className="text-center text-sm text-text-muted py-6">Sin datos.</div>;
  }
  return (
    <div className="flex items-center gap-6 flex-wrap">
      <svg width={160} height={160} viewBox="0 0 160 160">
        <circle cx={80} cy={80} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        {data.map((d, i) => {
          const len = (d.count / total) * c;
          const seg = (
            <circle
              key={d.label}
              cx={80}
              cy={80}
              r={radius}
              fill="none"
              stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 80 80)"
            />
          );
          offset += len;
          return seg;
        })}
        <text x={80} y={84} textAnchor="middle" className="fill-text font-extrabold" style={{ fontSize: 22 }}>
          {total}
        </text>
      </svg>
      <div className="flex-1 min-w-[160px] flex flex-col gap-1.5">
        {data.map((d, i) => (
          <div key={d.label} className="flex items-center gap-2 text-sm">
            <span
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }}
            />
            <span className="flex-1 truncate">{d.label}</span>
            <span className="font-semibold tabular-nums">{d.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI                                                                         */
/* -------------------------------------------------------------------------- */

const ACCENT: Record<string, string> = {
  indigo: 'bg-primary-soft text-primary',
  green: 'bg-success-soft text-success',
  red: 'bg-danger-soft text-danger',
  blue: 'bg-info-soft text-info',
};

function KpiCard({
  label,
  value,
  icon,
  accent,
  delta,
  deltaHint,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  accent: 'indigo' | 'green' | 'red' | 'blue';
  delta?: number | null;
  deltaHint?: string;
}): JSX.Element {
  const showDelta = delta !== undefined && delta !== null;
  const up = (delta ?? 0) >= 0;
  return (
    <div className="bg-surface border border-border rounded-lg p-5 shadow-sm flex flex-col gap-1.5 min-h-[120px]">
      <div className="flex items-start justify-between gap-2.5">
        <div className="text-text-muted text-[10.5px] font-bold uppercase tracking-widest">
          {label}
        </div>
        <div className={cn('w-9 h-9 rounded-md grid place-items-center flex-shrink-0', ACCENT[accent])}>
          {icon}
        </div>
      </div>
      <div className="text-[32px] font-extrabold tracking-tight leading-tight tabular-nums">
        {value}
      </div>
      {showDelta && (
        <div
          className={cn(
            'text-xs flex items-center gap-1 font-semibold',
            up ? 'text-success' : 'text-danger',
          )}
        >
          {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {up ? '+' : ''}
          {(delta ?? 0).toFixed(0)}%
          {deltaHint !== undefined && (
            <span className="text-text-muted font-normal">· {deltaHint}</span>
          )}
        </div>
      )}
    </div>
  );
}
